import React, { useState } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './Ticketing.css';

const TicketInsights = ({ show, onClose, insights, type = 'ticket', onTabSelect }) => {
  const [showAllUserTickets, setShowAllUserTickets] = useState(false);
  const [showAllCategoryTickets, setShowAllCategoryTickets] = useState(false);
  const [showAllLocationTickets, setShowAllLocationTickets] = useState(false);
  const [showAllReopenedTickets, setShowAllReopenedTickets] = useState(false);
  const [expandedNames, setExpandedNames] = useState({});

  if (!show) return null;

  // ─── Shared helpers ───────────────────────────────────────────────────────

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch { return 'N/A'; }
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return 'N/A';
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const toggleNameExpansion = (ticketId) => {
    setExpandedNames(prev => ({ ...prev, [ticketId]: !prev[ticketId] }));
  };

  const renderRequesterName = (name, ticketId) => {
    if (!name) return 'N/A';
    if (name.length <= 15) return <strong>{name}</strong>;
    const isExpanded = expandedNames[ticketId];
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${ticketId}`}>{name}</Tooltip>}>
        <strong style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleNameExpansion(ticketId); }}>
          {isExpanded ? name : truncateName(name)}
        </strong>
      </OverlayTrigger>
    );
  };

  // ─── Ticket mode renderers (unchanged) ───────────────────────────────────

  const renderTicketCard = (ticket) => (
    <Card key={ticket.id} className="insight-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <a href={`/ticketing/${ticket.id}`} className="ticket-link" target="_blank" rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/ticketing/${ticket.id}`, '_blank'); }}>
            {ticket.ticket_number}
          </a>
          <span className="ticket-date">{formatDate(ticket.created_on)}</span>
        </div>
        <p className="ticket-title">{ticket.subject || 'No subject'}</p>
        <div className="ticket-meta">
          <span className="meta-item">Status: <strong>{ticket.status || 'N/A'}</strong></span>
          <span className="meta-item">Priority: <strong>{ticket.priority || 'N/A'}</strong></span>
        </div>
      </Card.Body>
    </Card>
  );

  const renderCategoryTicketCard = (ticket) => (
    <Card key={ticket.id} className="insight-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <a href={`/ticketing/${ticket.id}`} className="ticket-link" target="_blank" rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/ticketing/${ticket.id}`, '_blank'); }}>
            {ticket.ticket_number}
          </a>
          <span className="ticket-date">{formatDate(ticket.created_on)}</span>
        </div>
        <p className="ticket-title">{ticket.subject || 'No subject'}</p>
        <div className="ticket-meta">
          <span className="meta-item">Requester: {renderRequesterName(ticket.requester_name, ticket.id)}</span>
          <span className="meta-item">Status: <strong>{ticket.status || 'N/A'}</strong></span>
        </div>
      </Card.Body>
    </Card>
  );

  // ─── Workflow mode renderer ───────────────────────────────────────────────

  const getStatusColor = (statusId) => {
  switch (Number(statusId)) {
    case 1: // New → Gray
      return { bg: '#f8fafc', color: '#475569', border: '#cbd5e1' };
    case 2: // In Progress → Blue
      return { bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' };
    case 3: // Waiting on End User → Orange
      return { bg: '#fbcab6', color: '#c13e09', border: '#f14d0b' };
    case 6: // Closed / Completed → Green
      return { bg: '#dcfce7', color: '#15803d', border: '#86efac' };
    default:
      return { bg: '#f8fafc', color: '#475569', border: '#cbd5e1' };
  }
};

const renderWorkflowStepCard = (ticket, index) => {
  const statusColors = getStatusColor(ticket.status?.id);
  const assigneeName = ticket.assigned_to?.name || 'Unassigned';

  return (
    <div key={ticket.ticket_id} className="workflow-step-card">
      {index > 0 && (
        <div className="workflow-step-connector">
          <div className="workflow-step-line" />
        </div>
      )}
      <Card
        className="insight-card"
        style={{
          borderLeft: `4px solid ${statusColors.border}`,
          cursor: onTabSelect ? 'pointer' : 'default',
          transition: 'box-shadow 0.2s',
          backgroundColor: '#fff',                         // ← always white bg
        }}
        onClick={() => onTabSelect?.(ticket.sequence_order)}
        onMouseEnter={e => {
          if (onTabSelect) e.currentTarget.style.boxShadow = `0 2px 8px ${statusColors.border}`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <Card.Body style={{ padding: '10px 12px' }}>

          {/* Row 1: sequence circle + dept name + arrow */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <span
              style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: statusColors.color,            // ← circle = status color
                color: '#fff', fontSize: '11px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {ticket.sequence_order}
            </span>
            <strong style={{ fontSize: '13px', color: '#212529' }}>
              {ticket.admin_group || 'N/A'}
            </strong>

            {onTabSelect && (
              <i
                className="fas fa-arrow-right ms-auto"
                style={{ fontSize: '11px', color: statusColors.color, opacity: 0.8 }}
              />
            )}
          </div>

          {/* Row 2: status badge + assignee */}
          <div className="d-flex align-items-center">
            <span
              style={{
                fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                borderRadius: '12px',
                background: statusColors.bg,               // ← soft bg
                color: statusColors.color,                 // ← status text color
                border: `1px solid ${statusColors.border}`,
                width: 'auto', textAlign: 'center', flexShrink: 0
              }}
            >
              {ticket.status?.name || 'N/A'}
            </span>
            <span style={{ fontSize: '11px', color: '#555', flex: 1, textAlign: 'right' }}>
              <i
                className="fas fa-user-circle me-1"
                style={{ color: statusColors.color }}       // ← user icon = status color
              />
              {renderRequesterName(assigneeName, `assignee-${ticket.ticket_id}`)}
            </span>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
};
  // ─── WORKFLOW panel ───────────────────────────────────────────────────────

  if (type === 'workflow') {
    const tickets = insights?.tickets
      ? [...insights.tickets].sort((a, b) => a.sequence_order - b.sequence_order)
      : [];
    const total  = tickets.length;
    const closed = tickets.filter(t => Number(t.status?.id) === 6).length;
    const pct    = total ? Math.round((closed / total) * 100) : 0;

    return (
      <>
        <div className="side-panel-overlay" onClick={onClose}></div>
        <div className="side-panel">
          <div className="side-panel-header">
            <div className="d-flex align-items-center gap-2">
              <i className="fas fa-lightbulb insight-header-icon"></i>
              <h5 className="mb-0">{insights?.workflow_number} - Info </h5>
            </div>
              <button className="close-btn insight-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="side-panel-content custom-scrollbar">
            {/* Progress summary */}
            <div className="insight-section">
              <div className="insight-section-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-tasks" insight-section-icon></i>
                  <h6 className="mb-0">Overall Progress</h6>
                </div>
                <span className="insight-badge">{closed}/{total}</span>
              </div>
              <Card className="insight-card">
                <Card.Body>
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: '12px', color: '#666' }}>
                    <span>{pct}% complete</span>
                    <span>{total - closed} remaining</span>
                  </div>
                  <div style={{ background: '#eee', borderRadius: '4px', height: '8px' }}>
                    <div style={{
                      width: `${pct}%`, background: pct === 100 ? '#2e7d32' : 'var(--color-primary-orange)',
                      height: '100%', borderRadius: '4px', transition: 'width 0.3s'
                    }} />
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Department steps */}
            <div className="insight-section">
              <div className="insight-section-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-sitemap insight-section-icon" ></i>
                  <h6 className="mb-0">Department Steps</h6>
                </div>
                <span className="insight-badge">{total}</span>
              </div>
              <div className="insight-cards">
                {tickets.length > 0 ? (
                  tickets.map((ticket, index) => renderWorkflowStepCard(ticket, index))
                ) : (
                  <Card className="insight-card">
                    <Card.Body><p className="text-muted text-center mb-0">No steps found</p></Card.Body>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── TICKET panel (completely unchanged) ──────────────────────────────────

  const sameUserTickets = insights?.same_user_tickets || [];
  const sameUserCount = parseInt(insights?.same_user_count || 0);
  const sameCategoryTickets = insights?.same_category_tickets || [];
  const sameCategoryCount = parseInt(insights?.same_category_count || 0);
  const sameLocationTickets = insights?.same_location_tickets || [];
  const sameLocationCount = parseInt(insights?.same_location_count || 0);
  const resolvedTickets = insights?.resolved_tickets || [];
  const resolvedCount = parseInt(insights?.resolved_tickets_count || 0);
  const reopenedTickets = insights?.reopened_tickets || [];
  const reopenedCount = parseInt(insights?.reopened_count || 0);

  const displayedUserTickets = showAllUserTickets ? sameUserTickets : sameUserTickets.slice(0, 1);
  const displayedCategoryTickets = showAllCategoryTickets ? sameCategoryTickets : sameCategoryTickets.slice(0, 1);
  const displayedLocationTickets = showAllLocationTickets ? sameLocationTickets : sameLocationTickets.slice(0, 1);
  const displayedReopenedTickets = showAllReopenedTickets ? reopenedTickets : reopenedTickets.slice(0, 1);

  return (
    <>
      <div className="side-panel-overlay" onClick={onClose}></div>
      <div className="side-panel">
        <div className="side-panel-header">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-lightbulb nsight-header-icon" ></i>
            <h5 className="mb-0">Ticket Insights</h5>
          </div>
          <button className="close-btn insight-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="side-panel-content custom-scrollbar">
          {/* Same User Tickets Section */}
          <div className="insight-section">
            <div className="insight-section-header">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-user insight-section-icon" ></i>
                <h6 className="mb-0">Same Requested User Tickets</h6>
              </div>
              <span className="insight-badge">{sameUserCount}</span>
            </div>
            <div className="insight-cards">
              {sameUserTickets.length > 0 ? (
                <>
                  {displayedUserTickets.map(renderTicketCard)}
                  {sameUserTickets.length > 1 && (
                    <button className="view-more-btn" onClick={() => setShowAllUserTickets(!showAllUserTickets)}>
                      {showAllUserTickets ? (<><i className="fas fa-chevron-up me-2"></i>View Less</>) : (<><i className="fas fa-chevron-down me-2"></i>View More ({sameUserTickets.length - 1} more)</>)}
                    </button>
                  )}
                </>
              ) : (
                <Card className="insight-card"><Card.Body><p className="text-muted text-center mb-0">No other tickets from this user</p></Card.Body></Card>
              )}
            </div>
          </div>

          {/* Same Category Tickets Section */}
          <div className="insight-section">
            <div className="insight-section-header">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-folder insight-section-icon" ></i>
                <h6 className="mb-0">Same Category Tickets</h6>
              </div>
              <span className="insight-badge">{sameCategoryCount}</span>
            </div>
            <div className="insight-cards">
              {sameCategoryTickets.length > 0 ? (
                <>
                  {displayedCategoryTickets.map(renderCategoryTicketCard)}
                  {sameCategoryTickets.length > 1 && (
                    <button className="view-more-btn" onClick={() => setShowAllCategoryTickets(!showAllCategoryTickets)}>
                      {showAllCategoryTickets ? (<><i className="fas fa-chevron-up me-2"></i>View Less</>) : (<><i className="fas fa-chevron-down me-2"></i>View More ({sameCategoryTickets.length - 1} more)</>)}
                    </button>
                  )}
                </>
              ) : (
                <Card className="insight-card"><Card.Body><p className="text-muted text-center mb-0">No same category tickets found</p></Card.Body></Card>
              )}
            </div>
          </div>

          {/* Same Location Tickets Section */}
          <div className="insight-section">
            <div className="insight-section-header">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-map-marker-alt insight-section-icon" ></i>
                <h6 className="mb-0">Same Location Tickets</h6>
              </div>
              <span className="insight-badge">{sameLocationCount}</span>
            </div>
            <div className="insight-cards">
              {sameLocationTickets.length > 0 ? (
                <>
                  {displayedLocationTickets.map(renderCategoryTicketCard)}
                  {sameLocationTickets.length > 1 && (
                    <button className="view-more-btn" onClick={() => setShowAllLocationTickets(!showAllLocationTickets)}>
                      {showAllLocationTickets ? (<><i className="fas fa-chevron-up me-2"></i>View Less</>) : (<><i className="fas fa-chevron-down me-2"></i>View More ({sameLocationTickets.length - 1} more)</>)}
                    </button>
                  )}
                </>
              ) : (
                <Card className="insight-card"><Card.Body><p className="text-muted text-center mb-0">No same location tickets found</p></Card.Body></Card>
              )}
            </div>
          </div>

          {/* Reopened Tickets Section */}
          <div className="insight-section">
            <div className="insight-section-header">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-redo insight-section-icon"></i>
                <h6 className="mb-0">Reopened Tickets</h6>
              </div>
              <span className="insight-badge">{reopenedCount}</span>
            </div>
            <div className="insight-cards">
              {reopenedTickets.length > 0 ? (
                <>
                  {displayedReopenedTickets.map(renderCategoryTicketCard)}
                  {reopenedTickets.length > 1 && (
                    <button className="view-more-btn" onClick={() => setShowAllReopenedTickets(!showAllReopenedTickets)}>
                      {showAllReopenedTickets ? (<><i className="fas fa-chevron-up me-2"></i>View Less</>) : (<><i className="fas fa-chevron-down me-2"></i>View More ({reopenedTickets.length - 1} more)</>)}
                    </button>
                  )}
                </>
              ) : (
                <Card className="insight-card"><Card.Body><p className="text-muted text-center mb-0">No reopened tickets found</p></Card.Body></Card>
              )}
            </div>
          </div>

          {/* Resolved Tickets Section */}
          <div className="insight-section">
            <div className="insight-section-header">
              <div className="d-flex align-items-center gap-2">
                <i className="fas fa-check-circle insight-section-icon" ></i>
                <h6 className="mb-0">Resolved Similar Issues</h6>
              </div>
              <span className="insight-badge">{resolvedCount}</span>
            </div>
            <div className="insight-cards">
              {resolvedTickets.length > 0 ? (
                resolvedTickets.slice(0, 1).map(renderCategoryTicketCard)
              ) : (
                <Card className="insight-card"><Card.Body><p className="text-muted text-center mb-0">No resolved similar issues found</p></Card.Body></Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketInsights;